<template>
    <div class="searchable-select position-relative" ref="dropdownRef">
        <div class="input-group">
            <input type="text" class="form-control" :placeholder="placeholder" v-model="searchQuery"
                @focus="openDropdown" @input="onInput" @keydown.down.prevent="navigateDown"
                @keydown.up.prevent="navigateUp" @keydown.enter.prevent="selectHighlighted"
                @keydown.escape="closeDropdown" />

            <button v-if="modelValue" class="btn btn-outline-secondary border-start-0" type="button"
                @click="clearSelection">
                <i class="bi bi-x-lg"></i>
            </button>

            <button class="btn btn-outline-secondary" type="button" @click="toggleDropdown">
                <i class="bi" :class="isOpen ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
            </button>
        </div>

        <div v-if="isOpen" class="dropdown-menu show w-100 shadow-sm mt-1 overflow-auto" style="max-height: 250px;">
            <div v-if="loading" class="text-center py-3 text-muted">
                <div class="spinner-border spinner-border-sm me-2" role="status"></div>
                Ładowanie danych...
            </div>

            <div v-else-if="filteredItems.length === 0" class="dropdown-item disabled text-muted">
                Brak wyników
            </div>

            <template v-else>
                <div v-for="(item, index) in filteredItems" :key="item.ID || item.makeID"
                    class="dropdown-item d-flex justify-content-between align-items-center cursor-pointer py-2" :class="{
                        'active-highlight': highlightedIndex === index,
                        'fw-bold': modelValue === (item.ID || item.makeID)
                    }" @click="selectItem(item)" @mouseenter="highlightedIndex = index">
                    <span>{{ item.name }}</span>

                    <i v-if="modelValue === (item.ID || item.makeID)" class="bi bi-check-lg text-primary fs-5"></i>
                </div>
            </template>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import apiClient from '@/api'

const props = defineProps({
    modelValue: {
        type: [String, Number],
        default: null
    },
    placeholder: {
        type: String,
        default: 'Marka pojazdu'
    }
})

const emit = defineEmits(['update:modelValue', 'change'])

const isOpen = ref(false)
const loading = ref(false)
const searchQuery = ref('')
const items = ref([])
const highlightedIndex = ref(-1)
const dropdownRef = ref(null)

const fetchData = async () => {
    loading.value = true
    try {
        const response = await apiClient.post({
                action: 'get-makes'
        })

        const data = await response.data
        items.value = Array.isArray(data) ? data : (data.data || [])
        updateSearchQueryFromModel()
    } catch (error) {
        console.error('Błąd podczas pobierania danych:', error)
    } finally {
        loading.value = false
    }
}

const filteredItems = computed(() => {
    if (!searchQuery.value) return items.value

    const selectedItem = items.value.find(i => (i.ID || i.makeID) === props.modelValue)
    if (selectedItem && selectedItem.name === searchQuery.value) {
        return items.value
    }

    return items.value.filter(item =>
        item.name.toLowerCase().includes(searchQuery.value.toLowerCase())
    )
})

const updateSearchQueryFromModel = () => {
    const selected = items.value.find(i => (i.ID || i.makeID) === props.modelValue)
    if (selected) {
        searchQuery.value = selected.name
    } else if (!props.modelValue) {
        searchQuery.value = ''
    }
}

const openDropdown = () => {
    isOpen.value = true
    highlightedIndex.value = -1
}

const closeDropdown = () => {
    isOpen.value = false
    highlightedIndex.value = -1
    updateSearchQueryFromModel()
}

const toggleDropdown = () => {
    if (isOpen.value) {
        closeDropdown()
    } else {
        openDropdown()
    }
}

const onInput = () => {
    if (!isOpen.value) openDropdown()
    highlightedIndex.value = 0
}

const navigateDown = () => {
    if (!isOpen.value) {
        openDropdown()
        return
    }
    if (highlightedIndex.value < filteredItems.value.length - 1) {
        highlightedIndex.value++
    }
}

const navigateUp = () => {
    if (highlightedIndex.value > 0) {
        highlightedIndex.value--
    }
}

const selectHighlighted = () => {
    if (isOpen.value && highlightedIndex.value >= 0 && filteredItems.value[highlightedIndex.value]) {
        selectItem(filteredItems.value[highlightedIndex.value])
    }
}

const selectItem = (item) => {
    const itemId = item.ID || item.makeID
    emit('update:modelValue', itemId)
    emit('change', item)
    searchQuery.value = item.name
    isOpen.value = false
}

const clearSelection = () => {
    emit('update:modelValue', null)
    emit('change', null)
    searchQuery.value = ''
    highlightedIndex.value = -1
}

const handleClickOutside = (event) => {
    if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
        closeDropdown()
    }
}

watch(() => props.modelValue, () => {
    updateSearchQueryFromModel()
})

onMounted(() => {
    fetchData()
    document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.cursor-pointer {
    cursor: pointer;
}
.active-highlight {
    background-color: #f8f9fa;
    color: #0d6efd;
}
.dropdown-item:active {
    background-color: #e9ecef;
    color: #212529;
}
</style>